/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { KAHU_AI_LINKS, KahuPredefinedPrompts } from '@/utils/constants';
import { formatKahuMessages } from '@/utils/formatKahuMessages';
import { copyLabelWithFact } from '@/utils/kahuCopyLabelWithFact';
import { useSearchHighlightText } from '@/ux-common/src/hook/useSearchHighlightText';
import Icons from '@/ux-common/src/icons/icons';
import { formatNumberByLocale } from '@/ux-common/src/lib/utils';
import { ChatMessage, kahuCopyInfoType } from 'types/kahu';

import { useKahu } from '@/hooks/useKahu';
import { useKahuDockState } from '@/hooks/useKahuDockState';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import SearchWithNavigation from '../../ux-common/src/components/common/search-with-navigation';
import { UnderscoreLink } from '../common/underscore-link';
import { truncateTraceId } from '../display-page/traces/traceUtils';
import { KahuIcons, OptimizationIcons } from '../icons/icons';
import BasicLoader from '../spinner/basic-loader';
import { KahuConversationList } from './kahu-conversation-list';
import { downloadConversationMarkdown } from './kahu-download-chat';
import { KahuErrorResponse } from './kahu-error-response';
import { KahuFloatingDockProvider } from './kahu-floating-dock-context';
import { KahuPredefinedPromptChip } from './kahu-predefined-prompt-chip';
import { KahuResponse } from './kahu-response';
import { KahuRowPromptWrapper } from './kahu-row-prompt-wrapper';
import { KahuUserPrompt } from './kahu-user-prompt';

const PAGE_SIZE = 20;

const KAHU_ICON_CSS_CLASS =
  'text-[#C1C9CF] group-active:text-white group-hover:text-white';

type ResizeHandle = 'top' | 'left' | 'top-left';

type KahuChattingInterfaceProps = {
  setWindowState: React.Dispatch<React.SetStateAction<'open' | 'minimized'>>;
  onResizeMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    handle: ResizeHandle
  ) => void;
};

export const KahuChattingInterface = ({
  setWindowState,
  onResizeMouseDown,
}: KahuChattingInterfaceProps) => {
  const [input, setInput] = useState('');
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);

  const [copiedInfo, setCopiedInfo] = useState<kahuCopyInfoType | null>(null);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isRevealing, setIsRevealing] = useState(false);

  const [totalTokenCount, setTotalTokenCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const copyTooltipTimerRef = useRef<number | null>(null);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Anchors for floating chat UI (list pager that docks above the input). Held
  // as state so the dock slot / scroll root reach consumers once they mount.
  const [dockEl, setDockEl] = useState<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);

  // holds the scroll height captured BEFORE we reveal more —>> used to keep scroll anchored.
  const prevScrollHeightRef = useRef<number>(0);

  // the sentinel element at the top of the message list.
  const topSentinelRef = useRef<HTMLDivElement | null>(null);

  // Track previous values so we only auto-scroll on genuinely new messages,
  // not when the user reveals older ones via windowing.
  const prevKahuMessageLengthRef = useRef(0);
  const prevIsSendingRef = useRef(false);

  const { isDocked, setDocked, toggleDocked } = useKahuDockState();
  const {
    messages: kahuMessage,
    sendMessage,
    isSending,
    sendingSessionId,
    activeConversationId,
    onCancelRequest,
    sessionId,
    hasMultipleConversations,
    currentView,
    goToConversationList,
    isSwitchingConversation,
    submitFeedback,
    draftPrompt,
    setDraftPrompt,
  } = useKahu();

  const draftPromptRef = useRef(draftPrompt);

  const { messages } = useLanguage();
  const KahuAiString = messages?.KahuAI;

  const kahuCopiedStrWithPrefix = copyLabelWithFact(copiedInfo);

  const isSendingHere = isSending && sendingSessionId === activeConversationId;

  const kahuCopiedConfirmationStr =
    copiedInfo?.copiedContentType === 'traceId'
      ? `Trace ID copied to clipboard`
      : copiedInfo?.copiedContentType === 'appName'
        ? `Application name copied to clipboard`
        : copiedInfo?.copiedContentType === 'workflowName'
          ? `Workflow name copied to clipboard`
          : '';

  const isDisabled = !isSending && input.trim().length === 0;

  const adjustTextAreaHeight = () => {
    const textArea = textAreaRef.current;

    if (textArea) {
      textArea.style.height = 'auto';
      const newHeight = Math.min(textArea.scrollHeight, 200);
      textArea.style.height = `${newHeight}px`;

      // Check if textarea has grown beyond single line (42px is min height)
      setIsMultiline(newHeight > 42);
    }
  };

  const handleSend = () => {
    const prompt = input.trim();
    if (!prompt || isSending) return;

    setPendingPrompt(prompt);
    sendMessage(prompt);
    setInput('');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(sessionId || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Pass the FULL array to search so search results always include all messages.
  const {
    term,
    setTerm,
    handleClearSearch,
    prevMatch,
    nextMatch,
    matches,
    currentIndex,
    beginHighlightPass,
    getHighlightedNodes,
  } = useSearchHighlightText(kahuMessage);

  // If there's no search term, we window messages for performance. When searching, we show all messages so matches can be highlighted in context.
  const hasSearchTerm = Boolean(term.trim());

  // When the search term changes, we need to re-run the highlight logic on all messages. So we call beginHighlightPass to trigger that logic in useSearchHighlightText.
  beginHighlightPass();

  // When there's a search term, we show all messages so that matches can be highlighted in context. Otherwise, we window messages for performance.
  const windowedKahuMessages = useMemo(() => {
    if (hasSearchTerm) return kahuMessage;
    return kahuMessage.slice(-visibleCount);
  }, [kahuMessage, visibleCount, hasSearchTerm]);

  // This is the array we actually render — full list when searching, otherwise
  // the windowed list. The provider already inserts a placeholder assistant
  // message (status: 'thinking') as soon as a send begins, so no synthetic
  // loader entry is needed here.
  const userPromptAndKahuResponseWithLoader: ChatMessage[] =
    windowedKahuMessages;

  // After visibleCount grows, restore scroll so the user doesn't get yanked to the top.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (!isRevealing) return;

    const newScrollHeight = container.scrollHeight;
    const delta = newScrollHeight - prevScrollHeightRef.current;
    if (delta > 0) {
      container.scrollTop = container.scrollTop + delta;
    }
    setIsRevealing(false);
  }, [visibleCount]);

  // When the user switches conversations, snap back to the latest 20.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [sessionId]);

  // Recompute the total token count whenever the message list changes. This is used to display the total number of tokens in the current conversation.
  useEffect(() => {
    setTotalTokenCount(
      kahuMessage.reduce((sum, message) => sum + (message.tokenCount ?? 0), 0)
    );
  }, [kahuMessage]);

  // Watch the top sentinel. When it's visible, reveal the next page of older messages.
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;
    if (hasSearchTerm) return; // search shows everything already
    if (visibleCount >= kahuMessage.length) return; // nothing left to load

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (isRevealing) return;

        // 1) capture current scroll height so we can restore position later.
        prevScrollHeightRef.current = container.scrollHeight;
        setIsRevealing(true);

        // 2) tiny delay so the skeleton actually flashes —> feels intentional.
        window.setTimeout(() => {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, kahuMessage.length));
        }, 350);
      },
      { root: container, rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, kahuMessage.length, hasSearchTerm, isRevealing]);

  // append copied text to input with prefix and keep focus in textarea with cursor at end.
  useEffect(() => {
    if (!copiedInfo?.id || !kahuCopiedStrWithPrefix) return;

    setInput((prev) => {
      const nextLine = kahuCopiedStrWithPrefix.trim();

      // If user already typed text, append on a new line. If input is empty, insert as first line.
      return prev.trim().length > 0 ? `${prev}\n${nextLine}` : nextLine;
    });

    // keep typing focus in textarea and move cursor to end.
    requestAnimationFrame(() => {
      const textArea = textAreaRef.current;
      if (!textArea) return;
      textArea.focus();
      const end = textArea.value.length;
      textArea.setSelectionRange(end, end);
    });
  }, [copiedInfo?.id, kahuCopiedStrWithPrefix]);

  // reset copiedInfo after 3 seconds so that tooltip goes away and user can copy again to see tooltip.
  useEffect(() => {
    if (!copiedInfo) return;

    if (copyTooltipTimerRef.current) {
      window.clearTimeout(copyTooltipTimerRef.current);
    }

    copyTooltipTimerRef.current = window.setTimeout(() => {
      setCopiedInfo(null);
      copyTooltipTimerRef.current = null;
    }, 2000);

    return () => {
      if (copyTooltipTimerRef.current) {
        window.clearTimeout(copyTooltipTimerRef.current);
      }
    };
  }, [copiedInfo?.id]);

  useEffect(() => {
    adjustTextAreaHeight();
  }, [input]);

  // Prefill the input with a prompt handed off from outside the chat (e.g. eval badge)
  // and focus the textarea with the cursor at the end.
  useEffect(() => {
    if (!draftPrompt) return;

    setInput(draftPrompt);
    setDraftPrompt(null);

    requestAnimationFrame(() => {
      const textArea = textAreaRef.current;
      if (!textArea) return;
      textArea.focus();
      const end = textArea.value.length;
      textArea.setSelectionRange(end, end);
    });
  }, [draftPrompt, setDraftPrompt]);

  useEffect(() => {
    draftPromptRef.current = draftPrompt;
  }, [draftPrompt]);

  useEffect(() => {
    if (!isSending) {
      setPendingPrompt('');
    }
  }, [isSending]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const newMessageAdded =
      kahuMessage.length > prevKahuMessageLengthRef.current;
    const startedSending = isSending && !prevIsSendingRef.current;

    if (newMessageAdded || startedSending) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }

    prevKahuMessageLengthRef.current = kahuMessage.length;
    prevIsSendingRef.current = isSending;
  }, [kahuMessage.length, isSending]);

  // show scroll to bottom button.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollButton(container.scrollTop >= 50 && distanceFromBottom > 10);
    };
    container.addEventListener('scroll', handleScroll);

    // Skip clearing when a draft prompt is waiting to be prefilled (e.g. on
    // the very first mount after opening chat from the eval badge).
    if (!draftPromptRef.current) {
      setInput('');
    }
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentView, isSwitchingConversation]);

  // Sync the scroll container into state so the floating-dock context can use
  // it as the IntersectionObserver root when the chat view (re)mounts.
  useEffect(() => {
    setScrollRoot(scrollContainerRef.current);
  }, [currentView, isSwitchingConversation]);

  const sendUserPrompt = (prompt: string) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isSending) return;
    setPendingPrompt(trimmedPrompt);
    sendMessage(trimmedPrompt);
  };

  const firstPrompt = userPromptAndKahuResponseWithLoader[0];
  const firstResponse = userPromptAndKahuResponseWithLoader[1];
  const hasFirstResponse =
    firstPrompt?.role === 'user' &&
    firstResponse?.role === 'assistant' &&
    firstResponse.status !== 'thinking';

  return (
    <div
      id="kahu-chat"
      className={`h-full w-full rounded-lg bg-[linear-gradient(180deg,#257C9D_0%,#34BCAF_100%)] p-px`}
    >
      <main
        className={`relative flex h-full w-full flex-col rounded-lg bg-canvas-background-secondary`}
      >
        {/* Resize Handles — top, left, and top-left corner only.
             The bottom-right corner is always the fixed anchor.
             Rendered in both floating and docked mode: grabbing a handle
             while docked triggers an instant Docked -> Floating transition. */}
        <>
          {/* Top edge — drag up to increase height */}
          <div
            className="absolute -top-1 left-0 h-2 w-full cursor-ns-resize"
            onMouseDown={(e) => onResizeMouseDown(e, 'top')}
          />
          {/* Left edge — drag left to increase width */}
          <div
            className="absolute -left-1 top-0 h-full w-2 cursor-ew-resize"
            onMouseDown={(e) => onResizeMouseDown(e, 'left')}
          />
          {/* Top-left corner — drag to resize both dimensions */}
          <div
            className="absolute -left-1 -top-1 h-3 w-3 cursor-nwse-resize"
            onMouseDown={(e) => onResizeMouseDown(e, 'top-left')}
          />
        </>

        <div className="flex flex-1 flex-col overflow-hidden">
          {/* chat window header */}
          <div
            className={`flex h-9 w-full items-center justify-between rounded-t-lg bg-[#257C9D] px-2 py-[6px]`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-1 pr-2">
              {/* Back button — shows when there are conversations to go back to */}
              {hasMultipleConversations && currentView === 'chat' && (
                <button
                  className="group grid h-6 w-6 shrink-0 place-items-center rounded bg-transparent p-0 hover:bg-[#FFFFFF1F]"
                  onClick={goToConversationList}
                >
                  {/* Left arrow / back icon */}
                  {KahuIcons.BackArrow(KAHU_ICON_CSS_CLASS)}
                </button>
              )}
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0000008F]">
                {KahuIcons.KahuInvocationIcon('w-[14px]')}
              </span>
              <p className="line-clamp-1 truncate text-h4 font-bold text-white">
                {currentView === 'conversation-list'
                  ? KahuAiString?.kahu_ai_agent
                  : hasFirstResponse
                    ? formatKahuMessages(firstPrompt.response)
                    : KahuAiString?.hey_im_kahu}
              </p>
            </div>
            <div className="flex shrink-0 gap-0.5">
              {currentView === 'chat' && windowedKahuMessages.length > 0 && (
                <button
                  className="group grid h-6 w-6 place-items-center rounded bg-transparent p-0 hover:bg-[#FFFFFF1F] active:bg-[#FFFFFF29]"
                  onClick={() =>
                    downloadConversationMarkdown(kahuMessage, { sessionId })
                  }
                  aria-label="download conversation"
                >
                  {KahuIcons.DownloadIcon(KAHU_ICON_CSS_CLASS)}
                </button>
              )}

              <button
                className="group grid h-6 w-6 place-items-center rounded bg-transparent p-0 hover:bg-[#FFFFFF1F] active:bg-[#FFFFFF29]"
                onClick={() => {
                  setDocked(false);
                  setWindowState('minimized');
                }}
                aria-label="minimize chat"
              >
                {KahuIcons.MinusIcon(KAHU_ICON_CSS_CLASS)}
              </button>
              <button
                className="group grid h-6 w-6 place-items-center rounded bg-transparent p-0 hover:bg-[#FFFFFF1F] active:bg-[#FFFFFF29]"
                onClick={toggleDocked}
                aria-label="toggle dock to maximize or float chat"
              >
                {isDocked
                  ? KahuIcons.MinimizeIcon(KAHU_ICON_CSS_CLASS)
                  : KahuIcons.MaximizeIcon(KAHU_ICON_CSS_CLASS)}
              </button>
            </div>
          </div>

          {currentView === 'chat' &&
            !isRevealing &&
            !isSwitchingConversation && (
              <div className="px-2 pb-3 pt-2">
                <SearchWithNavigation
                  value={term}
                  onChange={setTerm}
                  onClear={handleClearSearch}
                  searchWidth="w-full"
                  matches={matches}
                  currentIndex={currentIndex}
                  onPreviousMatch={prevMatch}
                  onNextMatch={nextMatch}
                  disabled={false}
                />
              </div>
            )}

          {/* Loading older messages skeleton */}
          {isRevealing && currentView === 'chat' && (
            <div className="flex w-full items-center justify-center gap-[10px] bg-[#061419] py-[10px]">
              <BasicLoader className="size-4" />
              <p className="text-center text-body text-body-subtle">
                {KahuAiString?.loading_previous_messages}...
              </p>
            </div>
          )}

          {/* Main chat container — flex layout, no fixed margin needed */}
          {currentView === 'conversation-list' ? (
            <KahuConversationList />
          ) : isSwitchingConversation ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-y-4">
              <BasicLoader className="size-10" />
              <div className="text-center">
                <p className="text-body-sm text-form-input-disabled">
                  {messages?.General?.please_wait}..
                </p>
              </div>
            </div>
          ) : (
            <KahuFloatingDockProvider value={{ dockEl, scrollRoot }}>
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4"
                ref={scrollContainerRef}
              >
                <div className="mb-4 rounded-md bg-white/[8%] px-3 py-4">
                  <span className="mb-1 text-body-lg font-medium text-[#EBEFF1]">
                    {messages?.InfoContainer.get_started}
                  </span>
                  <div className="flex flex-col gap-1">
                    {KAHU_AI_LINKS?.map((link) => (
                      <UnderscoreLink
                        key={link.href}
                        href={link.href}
                        text={link.text}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <KahuRowPromptWrapper>
                    <div className="relative py-2 pr-4">
                      <p className="text-body font-normal text-body-secondary">
                        {
                          KahuAiString?.ask_me_about_your_application_traces_prompts_and_errors
                        }
                      </p>
                      <span className="absolute right-full top-0 h-2 w-[6px]" />
                    </div>
                  </KahuRowPromptWrapper>
                  <div className="w-fit py-2 pr-4">
                    <p className="text-body font-normal text-body-secondary">
                      {KahuAiString?.here_are_some_examples}
                    </p>
                  </div>
                  {/* call the api on clicking on each of the predefined chips. */}
                  <div className="flex flex-col gap-1">
                    {KahuPredefinedPrompts.map((prompt) => (
                      <Fragment key={prompt}>
                        <KahuPredefinedPromptChip
                          prompt={prompt}
                          sendUserPrompt={sendUserPrompt}
                        />
                      </Fragment>
                    ))}
                  </div>
                  {userPromptAndKahuResponseWithLoader.length > 0 &&
                    (sessionId ? (
                      <div className="flex items-center justify-center gap-1 pt-4">
                        <div className="group relative">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="cursor-default">
                                <p className="text-center text-body-sm font-normal tracking-[2%] text-form-input-disabled">
                                  {KahuAiString?.session_id}:{' '}
                                  {truncateTraceId(sessionId)}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent className="rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white">
                                {KahuAiString?.session_id}: {sessionId}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <button
                            aria-label="Copy Session id"
                            className={`absolute -right-1 -top-1 flex h-5 w-5 translate-y-1/2 items-center justify-center rounded bg-[#08242F] transition-opacity ${
                              copied
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100'
                            }`}
                            onClick={handleCopy}
                          >
                            {copied
                              ? Icons.CopyGreenCheckIcon()
                              : Icons.CopyIcon()}
                          </button>
                        </div>
                        {totalTokenCount > 0 && (
                          <>
                            <span className="text-body-sm text-form-input-disabled">
                              {' • '}
                            </span>

                            <p className="text-center text-body-sm font-normal tracking-[2%] text-form-input-disabled">
                              {formatNumberByLocale(totalTokenCount)}{' '}
                              {KahuAiString?.tokens}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="mx-auto w-[141px] pt-4">
                        <Skeleton className="animate-pulse-custom h-4 rounded-lg bg-[#8C9EA1]" />
                      </div>
                    ))}

                  {/* Top sentinel — when this becomes visible, we load the next page. */}
                  {visibleCount < kahuMessage.length && !hasSearchTerm && (
                    <div ref={topSentinelRef} className="h-px w-full" />
                  )}

                  {userPromptAndKahuResponseWithLoader.map((message) => {
                    const highlightedMessage = hasSearchTerm
                      ? getHighlightedNodes(message.response)
                      : message.response;

                    if (message.role === 'user') {
                      return (
                        <KahuUserPrompt
                          className="ml-8 mt-5"
                          key={message.id}
                          message={highlightedMessage}
                        />
                      );
                    }
                    if (message.role === 'error') {
                      return (
                        <KahuRowPromptWrapper className="mt-5" key={message.id}>
                          <KahuErrorResponse
                            message={highlightedMessage}
                            duration={message.duration}
                          />
                        </KahuRowPromptWrapper>
                      );
                    }
                    return (
                      <KahuRowPromptWrapper className="mt-5" key={message.id}>
                        <KahuResponse
                          message={message.response}
                          messageId={message.id}
                          duration={message.duration}
                          status={message.status}
                          interimMessage={message.interimMessage}
                          copiedInfo={copiedInfo}
                          setCopiedInfo={setCopiedInfo}
                          highlightedMessage={
                            hasSearchTerm ? highlightedMessage : undefined
                          }
                          sendUserPrompt={sendUserPrompt}
                          followUpQuestions={message.followUpQuestions}
                          tokenCount={message.tokenCount}
                          timestamp={message.timestamp}
                          turnId={message.turnId}
                          feedback={message.feedback}
                          submitFeedback={
                            message.turnId && submitFeedback
                              ? async (rating) => {
                                  try {
                                    await submitFeedback(
                                      message.turnId!,
                                      rating,
                                      undefined
                                    );
                                  } catch (error) {
                                    console.error(
                                      'Failed to submit feedback:',
                                      error
                                    );
                                  }
                                }
                              : undefined
                          }
                        />
                      </KahuRowPromptWrapper>
                    );
                  })}
                </div>
                <div ref={bottomRef} />
              </div>

              {/* Input strip — normal flow at the bottom. Textarea growth shrinks scroll area naturally. */}
              {currentView === 'chat' && (
                <div className="relative px-4 pb-3">
                  {/* Floating overlays sit ABOVE the form via bottom-full */}
                  <div className="pointer-events-none absolute bottom-full left-0 right-0 flex flex-col px-4">
                    {showScrollButton && (
                      <div className="mb-2 flex justify-end">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => {
                                  const container = scrollContainerRef.current;
                                  if (!container) return;
                                  container.scrollTo({
                                    top: container.scrollHeight,
                                    behavior: 'smooth',
                                  });
                                }}
                                className="group pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-[#40565A]"
                              >
                                {KahuIcons.DownArrow(
                                  'group-hover:text-[#fff] text-[#C1C9CF]'
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {KahuAiString?.jump_to_latest}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                    {copiedInfo?.text && kahuCopiedConfirmationStr && (
                      <div className="pointer-events-auto flex items-center gap-2 rounded bg-[#6CF2A229] px-2 py-1 shadow-[0_6px_20px_rgba(0,0,0,0.28)] backdrop-blur-md">
                        <span>{OptimizationIcons.CheckCircleOutline()}</span>
                        <span className="text-body font-normal text-notification-success-light">
                          {kahuCopiedConfirmationStr}
                        </span>
                      </div>
                    )}
                    {/* Dock target: a list pager portals its floating copy
                        here when its inline pager scrolls out of view. */}
                    <div ref={setDockEl} className="pointer-events-auto" />
                  </div>

                  <form name="user-prompt">
                    <div className="flex gap-[14px]">
                      <div className="relative flex w-full items-end rounded-[4px] border border-line-secondary bg-card-00 focus-within:border-[#f8fafc] focus-within:ring-1 focus-within:ring-[#f8fafc]">
                        <textarea
                          ref={textAreaRef}
                          placeholder="How can i help you?"
                          className="max-h-[200px] min-h-[42px] w-full resize-none border-none bg-transparent px-3 py-[11px] pr-12 text-sm leading-[20px] outline-none [scrollbar-color:#6b7280_transparent] [scrollbar-width:thin]"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          rows={1}
                          name="user-prompt"
                        />

                        {isSendingHere ? (
                          <button
                            onClick={() => onCancelRequest(pendingPrompt)}
                            type="button"
                            aria-label="Cancel request"
                            className={`absolute right-[7px] flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-card-02 ${isMultiline ? 'bottom-[7px]' : 'top-1/2 -translate-y-1/2'}`}
                          >
                            {KahuIcons.StopIcon(`h-4 w-4`)}
                          </button>
                        ) : (
                          <button
                            onClick={handleSend}
                            disabled={isDisabled}
                            type="button"
                            aria-label="Send message"
                            className={`absolute right-[7px] flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-[linear-gradient(133.99deg,#20ACA1_10.91%,#257C9D_100%)] hover:bg-[linear-gradient(133.99deg,#68E0D1_10.91%,#2793B3_100%)] active:bg-[linear-gradient(133.99deg,#34BCAF_0%,#34BCAF_100%)] disabled:cursor-not-allowed disabled:bg-card-02 disabled:bg-none ${isMultiline ? 'bottom-[7px]' : 'top-1/2 -translate-y-1/2'}`}
                          >
                            {KahuIcons.ArrowRightIcon(
                              `h-4 w-4 ${isDisabled ? 'opacity-30' : 'opacity-100'}`
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </KahuFloatingDockProvider>
          )}
        </div>
      </main>
    </div>
  );
};
